import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { get, isBoolean, isNumber } from "lodash";
import PropTypes from "prop-types";

import { getOptionLabel } from "common/enums";

import Collapse from "./Collapse";

const mapBooleanToYesNo = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "-";
};

const FireLifeSafetyView = ({
  lifeSafety,
  isExpandable = true,
  t,
  isTracking = false,
}) => {
  const {
    isBasements,
    isQualifiedFireOfficer,
    isCompartmentationDone,
    isLightningArrestorInstalled,
    numberOfInternalStaircases,
    internalStaircaseWidth,
    numberOfExternalStaircases,
    isFireExitDoors,
    isBasementHasSprinklers,
    numberOfBasements,
    basementVentilationType,
    isAlternatePowerSupply,
    isExitSigns,
    assemblyPointDetail,
    fireOfficerName,
    fireOfficerMobileNumber,
    fireOfficerEmailId,
  } = lifeSafety;
  const fields = useMemo(
    () => [
      {
        name: "Whether any compartmentation has been done in the building?",
        key: "isCompartmentationDone",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isCompartmentationDone) : true,
      },
      {
        name: "Whether a lightning arrestor has been installed?",
        key: "isLightningArrestorInstalled",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isLightningArrestorInstalled) : true,
      },
      {
        name: "Number of internal staircase",
        key: "numberOfInternalStaircases",
        isRequired: true,
        isVisible: isTracking ? !!numberOfInternalStaircases : true,
      },
      {
        name: "Internal Staircase Width (Mtrs)",
        key: "internalStaircaseWidth",
        isRequired: true,
        isVisible: isTracking ? !!internalStaircaseWidth : true,
      },
      {
        name: "Number of External Staircase",
        key: "numberOfExternalStaircases",
        isRequired: true,
        isVisible: isTracking ? isNumber(numberOfExternalStaircases) : true,
      },
      {
        name: "Have fire exit doors been provided in building?",
        key: "isFireExitDoors",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isFireExitDoors) : true,
      },
      {
        name: "Are there any basements in the building?",
        key: "isBasements",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isBasements) : true,
      },
      {
        name: "Are basements sprinklered?",
        key: "isBasementHasSprinklers",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking
          ? isBoolean(isBasementHasSprinklers)
          : isBasements,
      },
      {
        name: "Number of basement provided",
        key: "numberOfBasements",
        isRequired: true,
        isVisible: isTracking ? isNumber(numberOfBasements) : isBasements,
      },
      {
        name: "Type of Ventilation",
        key: "basementVentilationType",
        renderField: (value) =>
          getOptionLabel("fireVentilationTypeEnum", value) || "-",
        isVisible: isTracking ? !!basementVentilationType : isBasements,
      },
      {
        name: "Whether adequate illumination with alternate power supply is provided for safe movement of persons towards and through the exits?",
        key: "isAlternatePowerSupply",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isAlternatePowerSupply) : true,
      },
      {
        name: "Whether proper exit signage has been provided in exit access area to provide clear directions to people moving towards exits?",
        key: "isExitSigns",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isExitSigns) : true,
      },
      {
        name: "Assembly Point Details",
        key: "assemblyPointDetail",
        isRequired: false,
        isVisible: isTracking ? !!assemblyPointDetail : true,
      },
      {
        name: "Is building have appointed qualified Fire Officer?",
        key: "isQualifiedFireOfficer",
        renderField: (value) => mapBooleanToYesNo(value),
        isVisible: isTracking ? isBoolean(isQualifiedFireOfficer) : true,
      },
      {
        name: "Fire Officer Name",
        key: "fireOfficerName",
        isRequired: true,
        isVisible: isTracking ? !!fireOfficerName : isQualifiedFireOfficer,
      },
      {
        name: "Mobile Number",
        key: "fireOfficerMobileNumber",
        isRequired: true,
        isVisible: isTracking
          ? !!fireOfficerMobileNumber
          : isQualifiedFireOfficer,
      },
      {
        name: "Email",
        key: "fireOfficerEmailId",
        isRequired: true,
        isVisible: isTracking ? !!fireOfficerEmailId : isQualifiedFireOfficer,
      },
    ],
    [
      isBasements,
      isQualifiedFireOfficer,
      isCompartmentationDone,
      isLightningArrestorInstalled,
      numberOfInternalStaircases,
      internalStaircaseWidth,
      numberOfExternalStaircases,
      isFireExitDoors,
      isBasementHasSprinklers,
      numberOfBasements,
      basementVentilationType,
      isAlternatePowerSupply,
      isExitSigns,
      assemblyPointDetail,
      fireOfficerName,
      fireOfficerMobileNumber,
      fireOfficerEmailId,
    ]
  );

  const isCollapseVisible = useMemo(
    () => fields.some(({ isVisible }) => isVisible),
    [fields]
  );

  return (
    <>
      {isCollapseVisible && (
        <Collapse title="Fire Life Safety" isExpandable={isExpandable}>
          <Row className="d-flex mb-2">
            {fields.map(({ name, isVisible, key, isRequired, renderField }) => {
              const value = get(lifeSafety, key, "");
              if (typeof isVisible !== "boolean" || isVisible) {
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : get(lifeSafety, key, null) ??
                            (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              }
            })}
          </Row>
        </Collapse>
      )}
    </>
  );
};

FireLifeSafetyView.propTypes = {
  lifeSafety: PropTypes.object,
  isExpandable: PropTypes.bool,
  isTracking: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(FireLifeSafetyView);
